/**
 * Types for the Proposals/Estimates feature
 */

export type ProposalStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';

export type ProposalType = 'residential' | 'commercial' | 'other_projects';

export type TierType = 'good' | 'better' | 'best';

/**
 * What an organization calls each tier. The slugs above are fixed, but the
 * names reps and customers see are configurable in Proposal Settings.
 */
export type TierLabelMap = Record<TierType, string>;

export interface ProposalTierLabel {
  tier_type: TierType;
  label: string;
  /** The built-in name ('Good'/'Better'/'Best') this tier falls back to */
  default_label: string;
  /** False when the org is still using default_label */
  is_custom: boolean;
}

export const DEFAULT_TIER_LABELS: TierLabelMap = {
  good: 'Good',
  better: 'Better',
  best: 'Best',
};

/** Canonical display order for the three tiers */
export const TIER_TYPES: TierType[] = ['good', 'better', 'best'];

export type PageType =
  | 'title'
  | 'introduction'
  | 'manufacturer_resource'
  | 'inspection'
  | 'measurements'
  | 'quote_details'
  | 'authorization'
  | 'terms'
  | 'custom'
  | 'manufacturer_marketing'
  | 'manufacturer_colors'
  | 'manufacturer_other';

export type ResourceType =
  | 'spec_sheet'
  | 'warranty'
  | 'brochure'
  | 'certification'
  | 'color_chart'
  | 'installation_guide'
  | 'other';

export interface ManufacturerResource {
  id: number;
  manufacturer: number;
  manufacturer_name: string;
  title: string;
  resource_type: ResourceType;
  pdf_file: string;
  description: string;
  is_global: boolean;
  organization: string | null;
  organization_name: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ManufacturerProductOption {
  id: number;
  title: string;
  description: string;
  image: string | null;
  image_url: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface ManufacturerProductWithOptions {
  id: number;
  manufacturer: number;
  manufacturer_name: string;
  name: string;
  description: string;
  sku: string;
  warranty_years: number | null;
  is_residential: boolean;
  is_commercial: boolean;
  is_other: boolean;
  is_active: boolean;
  sort_order: number;
  color_options_pdf: string | null;
  sell_sheet: string | null;
  options: ManufacturerProductOption[];
}

export interface ProposalLineItem {
  id: number;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  sort_order: number;
  manufacturer: number | null;
  manufacturer_name: string | null;
  product: number | null;
  product_details: ManufacturerProductWithOptions | null;
}

export interface ManufacturerProductDetails {
  id: number;
  name: string;
  manufacturer: number;
  manufacturer_name: string;
  warranty_years: number | null;
  sku: string;
  price_per_square: string | null;
  minimum_price_per_square: string | null;
}

export interface ProposalTier {
  id: number;
  tier_type: TierType;
  /** The org's name for this tier — use this, never the raw tier_type */
  tier_type_label: string;
  /** Server-derived permission for the current authenticated user. */
  can_override_minimum_price?: boolean;
  name: string;
  description: string;
  advantages: string[];
  manufacturer_product: number | null;
  manufacturer_product_details: ManufacturerProductDetails | null;
  product_override: boolean;
  use_manual_pricing: boolean;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  is_recommended: boolean;
  sort_order: number;
  line_items: ProposalLineItem[];
}

export interface ProposalPage {
  id: number;
  page_type: PageType;
  title: string;
  intro_text: string;
  is_enabled: boolean;
  sort_order: number;
  manufacturer_resource: number | null;
  manufacturer_resource_title: string | null;
  content: Record<string, unknown>;
  custom_image: string | null;
}

export interface ProposalOptionalUpgrade {
  is_required?: boolean;
  id: number;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  is_selected: boolean;
  sort_order: number;
}

export interface ProposalInspectionPhotoTag {
  id: number;
  name: string;
  color: string;
}

export interface ProposalInspectionPhoto {
  id: number;
  photo: number;
  photo_url: string | null;
  photo_thumbnail: string | null;
  annotations?: unknown;
  description: string;
  tags: ProposalInspectionPhotoTag[];
  sort_order: number;
}

export interface ProposalMeasurementsPdf {
  id: number;
  file_url: string;
  original_filename: string;
}

export interface ProposalRoofSection {
  id: number;
  name: string;
  polygon_coordinates: { lat: number; lng: number }[];
  area_sqft: number | null;
  color: string;
}

export interface ProposalLinearTotal {
  type: string;
  label: string;
  total_ft: number;
  color: string;
}

export interface ProposalNativeMeasurements {
  address: string;
  latitude: number | null;
  longitude: number | null;
  total_area_sqft: number;
  total_sections: number;
  total_lines: number;
  roof_sections: ProposalRoofSection[];
  linear_totals: ProposalLinearTotal[];
  eave_perimeter_ft: number;
  static_map_url: string | null;
}

export interface ProposalMeasurements {
  pdfs: ProposalMeasurementsPdf[];
  native: ProposalNativeMeasurements | null;
  summary?: {
    total_area_sqft: number;
    total_squares: number;
    waste_percent: number | null;
    squares_with_waste: number | null;
    predominant_pitch: string;
  } | null;
}

export interface ProposalListItem {
  id: number;
  proposal_number: string;
  title: string;
  status: ProposalStatus;
  proposal_type: ProposalType;
  proposal_type_display: string;
  opportunity: number;
  opportunity_name: string;
  contact: number | null;
  contact_name: string | null;
  created_by: number | null;
  created_by_name: string | null;
  featured_manufacturer: number | null;
  featured_manufacturer_name: string | null;
  proposal_date: string;
  valid_until: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  acceptance_date: string | null;
  accepted_tier: number | null;
  accepted_tier_name: string | null;
  selected_option_names: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationDetails {
  id: string;
  name: string;
  phone: string;
  email: string;
  website: string | null;
  logo: string | null;
  address: string;
}

export interface FeaturedManufacturerDetails {
  id: number;
  name: string;
  logo: string | null;
  general_marketing_material: string | null;
  colors_and_options: string | null;
  other_marketing_assets: string | null;
}

export interface ContactDetails {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface ProposalDetail {
  service_configuration?: import('../shared/proposal-services').ServiceConfiguration;
  service_version?: string;
  service_base_version?: string;
  presentation?: import("./proposalPresentation").ProposalPresentation;
  presentation_options?: import("./proposalPresentation").PresentationOptions;
  measurement_context?: Record<string, unknown>;
  id: number;
  proposal_number: string;
  public_token: string;
  public_url: string;
  proposal_type: ProposalType;
  proposal_type_display: string;
  /** The org's names for the three tiers */
  tier_labels: TierLabelMap;
  total_squares: number | null;
  show_total_squares: boolean;
  opportunity: number;
  opportunity_name: string;
  opportunity_address: string | null;
  contact: number | null;
  contact_name: string | null;
  contact_details: ContactDetails | null;
  created_by: number | null;
  created_by_name: string | null;
  organization_details: OrganizationDetails;
  featured_manufacturer: number | null;
  featured_manufacturer_details: FeaturedManufacturerDetails | null;
  status: ProposalStatus;
  title: string;
  report_type: string;
  proposal_date: string;
  cover_image: string | null;
  street_view_url: string | null;
  secondary_logo: string | null;
  introduction_text: string;
  scope_of_work: string[];
  terms_and_conditions: string;
  financing_enabled: boolean;
  financing_options: unknown[];
  tax_enabled: boolean;
  tax_rate: number;
  valid_until: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_tier: number | null;
  acceptance_name: string;
  acceptance_date: string | null;
  pdf_file: string | null;
  pdf_generated_at: string | null;
  notes: string;
  pages: ProposalPage[];
  tiers: ProposalTier[];
  optional_upgrades: ProposalOptionalUpgrade[];
  inspection_photos: ProposalInspectionPhoto[];
  measurements: ProposalMeasurements;
  created_at: string;
  updated_at: string;
}

export interface ProposalCreateInput {
  service_configuration?: import("../shared/proposal-services").ServiceConfiguration;
  measurement_context?: Record<string, unknown>;
  opportunity: number;
  contact?: number;
  title?: string;
  report_type?: string;
  proposal_type?: ProposalType;
  proposal_date?: string;
  introduction_text?: string;
  scope_of_work?: string[];
  terms_and_conditions?: string;
  financing_enabled?: boolean;
  financing_options?: unknown[];
  valid_until?: string;
  notes?: string;
  featured_manufacturer?: number | null;
}

export interface ProposalAcceptanceInput {
  service_selections?: import('../shared/proposal-services').ServiceSelections;
  offer_version?: string;
  edition_id?: number;
  source_version?: string;
  tier_id: number | null;
  signature: string;
  name: string;
  selected_upgrades?: number[];
  product_selections?: Record<string, number>; // product_id -> option_id
  comments?: string;
}

export interface PageBulkUpdate {
  id: number;
  is_enabled?: boolean;
  sort_order?: number;
}

// Template types for Proposal Settings

export interface ProposalTypeChoice {
  value: ProposalType;
  label: string;
}

export interface ProposalTemplateDefaultLineItem {
  id?: number;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  sort_order: number;
}

export interface ProposalTemplateDefaultTier {
  id?: number;
  tier_type: TierType;
  /** Read-only: the org's name for this tier, resolved by the API */
  tier_type_label?: string;
  name: string;
  description: string;
  advantages: string[];
  tax_rate: number;
  is_recommended: boolean;
  sort_order: number;
  default_line_items: ProposalTemplateDefaultLineItem[];
}

export interface ProposalTemplateDefaultUpgrade {
  is_required?: boolean;
  id?: number;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  sort_order: number;
}

export interface DefaultFeaturedManufacturerDetails {
  id: number;
  name: string;
  logo: string | null;
}

export interface ProposalTemplate {
  presentation_options?: import("./proposalPresentation").PresentationOptions;
  id: number;
  proposal_type: ProposalType;
  proposal_type_display: string;
  name: string;
  description: string;
  default_introduction_text: string;
  default_terms_and_conditions: string;
  default_featured_manufacturer: number | null;
  default_featured_manufacturer_details: DefaultFeaturedManufacturerDetails | null;
  show_total_squares: boolean;
  is_active: boolean;
  sort_order: number;
  default_tiers: ProposalTemplateDefaultTier[];
  default_upgrades: ProposalTemplateDefaultUpgrade[];
  created_at: string;
  updated_at: string;
}

export interface ProposalTemplateInput {
  presentation_options?: import("./proposalPresentation").PresentationOptions;
  proposal_type: ProposalType;
  name: string;
  description?: string;
  default_introduction_text?: string;
  default_terms_and_conditions?: string;
  default_featured_manufacturer?: number | null;
  show_total_squares?: boolean;
  is_active?: boolean;
  sort_order?: number;
  default_tiers?: Omit<ProposalTemplateDefaultTier, 'id'>[];
  default_upgrades?: Omit<ProposalTemplateDefaultUpgrade, 'id'>[];
}

// Public proposal view types (for /p/{token} endpoint)
export interface PublicProposal {
  service_configuration?: import('../shared/proposal-services').ServiceConfiguration;
  project_offer?: {version: string; terms: string};
  presentation?: import("./proposalPresentation").ProposalPresentation;
  presentation_options?: import("./proposalPresentation").PresentationOptions;
  acceptance_workflow?: import('../pages/proposals/ProposalWorkflow').AcceptanceWorkflow;
  id: number;
  proposal_number: string;
  title: string;
  report_type: string;
  /** The org's names for the three tiers — this page is public, so it can't fetch them */
  tier_labels: TierLabelMap;
  proposal_date: string;
  total_squares: number | null;
  show_total_squares: boolean;
  organization_details: Omit<OrganizationDetails, 'id'>;
  contact_name: string | null;
  opportunity_address: string | null;
  street_view_url: string | null;
  featured_manufacturer_details: FeaturedManufacturerDetails | null;
  cover_image: string | null;
  secondary_logo: string | null;
  introduction_text: string;
  scope_of_work: string[];
  terms_and_conditions: string;
  financing_enabled: boolean;
  financing_options: unknown[];
  tax_enabled: boolean;
  tax_rate: number;
  valid_until: string | null;
  status: ProposalStatus;
  tiers: ProposalTier[];
  optional_upgrades: ProposalOptionalUpgrade[];
  inspection_photos: ProposalInspectionPhoto[];
  measurements: ProposalMeasurements;
  pages: ProposalPage[];
  accepted_tier: number | null;
  acceptance_date: string | null;
}

// Manufacturer Product types (for Default Pricing)
export interface ManufacturerProduct {
  id: number;
  manufacturer: number;
  manufacturer_name: string;
  name: string;
  description: string;
  sku: string;
  warranty_years: number | null;
  is_residential: boolean;
  is_commercial: boolean;
  is_other: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface OrganizationProductPricing {
  id: number;
  product: number;
  product_details: ManufacturerProduct;
  price_per_square: string;
  minimum_price_per_square: string | null;
  tier_type: TierType | null;
  tier_type_display: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationProductPricingInput {
  product: number;
  price_per_square: string;
  minimum_price_per_square?: string | null;
  tier_type?: TierType | null;
}

export interface ManufacturerWithProducts {
  id: number;
  name: string;
  logo: string | null;
  products: Array<ManufacturerProduct & {
    pricing: {
      id: number | null;
      price_per_square: string | null;
      minimum_price_per_square: string | null;
      tier_type: TierType | null;
    } | null;
  }>;
}
